import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { FileSpreadsheet, FileText, X } from "lucide-react";
import { type KeyboardEvent, useEffect, useLayoutEffect, useRef } from "react";

export interface DocumentViewerTabStripItem {
    id: string;
    title: string;
    kind?: "document" | "spreadsheet";
}

interface DocumentViewerTabStripProps {
    tabs: readonly DocumentViewerTabStripItem[];
    activeTabId: string | null;
    onActivate: (id: string) => void;
    onClose: (id: string) => void;
}

export const DocumentViewerTabStrip = ({
    tabs,
    activeTabId,
    onActivate,
    onClose,
}: DocumentViewerTabStripProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const tabElementsRef = useRef(new Map<string, HTMLButtonElement>());
    const closedTabIdRef = useRef<string | null>(null);

    const requestClose = (id: string) => {
        closedTabIdRef.current = id;
        onClose(id);
        // A close lands in the update its request starts; a tab still mounted after that update was refused.
        setTimeout(() => {
            if (closedTabIdRef.current === id && tabElementsRef.current.has(id))
                closedTabIdRef.current = null;
        }, 0);
    };

    useEffect(() => {
        const closedTabId = closedTabIdRef.current;
        if (
            closedTabId === null ||
            activeTabId === null ||
            tabs.some((tab) => tab.id === closedTabId)
        )
            return;
        closedTabIdRef.current = null;
        const root = rootRef.current;
        const focused = document.activeElement;
        const focusIsFree =
            focused === null ||
            focused === document.body ||
            (root !== null && root.contains(focused));
        if (focusIsFree) tabElementsRef.current.get(activeTabId)?.focus();
    }, [tabs, activeTabId]);

    const tabLayout = JSON.stringify(tabs.map((tab) => [tab.id, tab.title]));
    useLayoutEffect(() => {
        const scroller = scrollerRef.current;
        const tab =
            activeTabId === null
                ? undefined
                : tabElementsRef.current.get(activeTabId);
        if (!scroller || !tab) return;
        const view = scroller.getBoundingClientRect();
        const box = tab.getBoundingClientRect();
        if (box.left < view.left) scroller.scrollLeft -= view.left - box.left;
        else if (box.right > view.right)
            scroller.scrollLeft += box.right - view.right;
    }, [tabLayout, activeTabId]);

    const handleTabKeyDown = (
        event: KeyboardEvent<HTMLButtonElement>,
        id: string
    ) => {
        if (event.key !== "Delete") return;
        event.preventDefault();
        requestClose(id);
    };

    return (
        <div
            ref={rootRef}
            data-slot="document-viewer-tab-strip"
            className="bg-muted/30 shrink-0 border-b"
        >
            <div
                ref={scrollerRef}
                className="overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:thin]"
            >
                <Tabs
                    value={activeTabId}
                    onValueChange={(value) => {
                        const tab = tabs.find((item) => item.id === value);
                        if (tab) onActivate(tab.id);
                    }}
                    className="grid w-max min-w-full gap-0"
                    style={{
                        gridTemplateColumns: `repeat(${tabs.length}, max-content)`,
                    }}
                >
                    <TabsList
                        variant="line"
                        aria-label="Open documents"
                        className="col-span-full row-start-1 grid h-auto w-auto grid-cols-subgrid items-stretch justify-start gap-0 rounded-none p-0 group-data-horizontal/tabs:h-auto"
                    >
                        {tabs.map((tab) => {
                            const isActive = tab.id === activeTabId;
                            const Glyph =
                                tab.kind === "spreadsheet"
                                    ? FileSpreadsheet
                                    : FileText;
                            return (
                                <TabsTrigger
                                    key={tab.id}
                                    value={tab.id}
                                    title={tab.title}
                                    ref={(
                                        element: HTMLButtonElement | null
                                    ) => {
                                        if (element)
                                            tabElementsRef.current.set(
                                                tab.id,
                                                element
                                            );
                                        else
                                            tabElementsRef.current.delete(
                                                tab.id
                                            );
                                    }}
                                    onKeyDown={(event) =>
                                        handleTabKeyDown(event, tab.id)
                                    }
                                    className="hover:bg-muted/60 data-active:text-foreground h-11 flex-none justify-start gap-2 rounded-none border-0 pr-12 pl-3 font-normal after:inset-x-0 after:h-0.5 group-data-horizontal/tabs:after:bottom-0 focus-visible:ring-inset data-active:font-semibold data-active:after:opacity-100 sm:h-9 sm:pr-9"
                                >
                                    <Glyph
                                        aria-hidden
                                        className={cn(
                                            "size-4",
                                            isActive
                                                ? "text-primary"
                                                : "text-muted-foreground"
                                        )}
                                    />
                                    <span className="grid max-w-36 sm:max-w-52">
                                        <span
                                            aria-hidden
                                            className="invisible col-start-1 row-start-1 truncate font-semibold"
                                        >
                                            {tab.title}
                                        </span>
                                        <span className="col-start-1 row-start-1 truncate">
                                            {tab.title}
                                        </span>
                                    </span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                    {/* The triggers are positioned; without its own stacking level this layer paints and hit-tests beneath them. */}
                    <div className="pointer-events-none relative z-10 col-span-full row-start-1 grid grid-cols-subgrid">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Close ${tab.title}`}
                                tabIndex={tab.id === activeTabId ? 0 : -1}
                                onClick={() => requestClose(tab.id)}
                                className="text-muted-foreground hover:text-foreground pointer-events-auto size-11 self-center justify-self-end rounded-md focus-visible:ring-inset sm:mr-1 sm:size-7"
                            >
                                <X aria-hidden />
                            </Button>
                        ))}
                    </div>
                </Tabs>
            </div>
        </div>
    );
};
